/**
 * Shared error-code registry — the single source of truth for every failure
 * the API can return.
 *
 * The code is the join key across the observability stack: the same value
 * appears in the HTTP error envelope, as a Prometheus label, a Sentry tag,
 * and a structured log field. Codes are stable and machine-readable; client
 * UX behavior keys off them instead of parsing message prose.
 *
 * The `class` field decides how loudly a failure is reported:
 *
 * - "expected": the app worked correctly; the request couldn't be satisfied
 *   (caller error, missing resource, org not configured). Counted in metrics
 *   and logged, never alerted on individually — only rate spikes matter.
 * - "upstream": an external dependency failed and we handled it. Grouped in
 *   Sentry as a warning (one issue per outage), alerted with debouncing.
 * - "bug": our code is wrong. Full capture, immediate alerting.
 *   HTTP 500 means bug and nothing else — a 502/504 spike says "check the
 *   vendor", a 500 spike says "roll back".
 *
 * Never throw INTERNAL_ERROR deliberately: it is the name the error funnel
 * assigns to unclassified surprises. If you want to throw it, what you
 * actually want is a new code in this registry.
 */

export type ErrorClass = "expected" | "upstream" | "bug";

export interface ErrorCodeDefinition {
  /** HTTP status this code maps to. */
  status: number;
  /** Whether a client may automatically retry the request. */
  retryable: boolean;
  /** Reporting class — see module doc. */
  class: ErrorClass;
}

export const ERROR_CODES = {
  // ── Caller's problem (4xx) ──────────────────────────────────────────────
  /** Request body/params failed validation. Fixing the request fixes it. */
  VALIDATION_ERROR: { status: 400, retryable: false, class: "expected" },
  /** Request references a related resource that doesn't exist (FK violation). */
  INVALID_REFERENCE: { status: 400, retryable: false, class: "expected" },
  /** Generic caller error when nothing more specific applies. */
  BAD_REQUEST: { status: 400, retryable: false, class: "expected" },
  /** Not signed in. */
  UNAUTHENTICATED: { status: 401, retryable: false, class: "expected" },
  /** Signed in, but not allowed to do this. */
  FORBIDDEN: { status: 403, retryable: false, class: "expected" },
  /** Signed in, but no organization membership/access. */
  NO_ORG_ACCESS: { status: 403, retryable: false, class: "expected" },
  /** Resource doesn't exist — including org-not-found, everywhere. */
  NOT_FOUND: { status: 404, retryable: false, class: "expected" },
  /** Resource already exists (unique violation). */
  DUPLICATE_RESOURCE: { status: 409, retryable: false, class: "expected" },
  /** Request is fine, but the org's data/setup can't satisfy it. */
  ORG_NOT_CONFIGURED: { status: 422, retryable: false, class: "expected" },
  /** Org has no Hajoca branch/profit-center mapping for this operation. */
  BRANCH_MAPPING_MISSING: { status: 422, retryable: false, class: "expected" },
  /** Too many requests. */
  RATE_LIMITED: { status: 429, retryable: true, class: "expected" },

  // ── Our bug (5xx) ───────────────────────────────────────────────────────
  /** Unclassified failure — assigned by the funnel, never thrown on purpose. */
  INTERNAL_ERROR: { status: 500, retryable: false, class: "bug" },
  /** Server is missing configuration it needs (env vars etc.). */
  CONFIG_ERROR: { status: 503, retryable: false, class: "bug" },
  /** Feature is turned off for this org/environment. */
  FEATURE_DISABLED: { status: 503, retryable: false, class: "expected" },

  // ── Upstream's problem (502/504) ────────────────────────────────────────
  /** Hajoca API returned an error. */
  HAJOCA_ERROR: { status: 502, retryable: true, class: "upstream" },
  /** Hajoca rejected our credentials/role (e.g. inventory API not granted). */
  HAJOCA_NOT_AUTHORIZED: { status: 502, retryable: false, class: "upstream" },
  /** Hajoca did not respond in time. */
  HAJOCA_TIMEOUT: { status: 504, retryable: true, class: "upstream" },
  /** Tax calculation provider failed. */
  TAX_UPSTREAM_ERROR: { status: 502, retryable: true, class: "upstream" },
  /** LLM provider returned an error. */
  LLM_ERROR: { status: 502, retryable: true, class: "upstream" },
  /** LLM provider did not respond in time. */
  LLM_TIMEOUT: { status: 504, retryable: true, class: "upstream" },
  /** Email provider (Loops) failed. */
  EMAIL_ERROR: { status: 502, retryable: true, class: "upstream" },
  /** SMS provider (AWS) failed. */
  SMS_ERROR: { status: 502, retryable: true, class: "upstream" },
  /** Auth provider (Clerk) failed. */
  AUTH_UPSTREAM_ERROR: { status: 502, retryable: true, class: "upstream" },
} as const satisfies Record<string, ErrorCodeDefinition>;

export type ErrorCode = keyof typeof ERROR_CODES;

/**
 * The wire contract: shape of every non-2xx API response body.
 * Extends the pre-existing `{ success: false, error }` shape, so both legacy
 * reader styles (`body.error` string, `body.success === false`) keep working.
 */
export interface ApiErrorBody {
  success: false;
  /** Human-readable message, safe to show a user. Never raw upstream bodies. */
  error: string;
  code: ErrorCode;
  /** Request-scoped id (also sent as the X-Request-Id header). Joins a user
   * report to its Loki logs and Sentry event. */
  requestId?: string;
  /** Client may auto-retry iff true. */
  retryable: boolean;
  /** Optional extra detail (e.g. validation issues). */
  details?: unknown;
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === "string" && value in ERROR_CODES;
}
